import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Progress } from '@/components/ui/progress'
import { isSelected, type Selection, type SelectionInput } from '@/lib/cross-filter'
import { formatFieldValue, shortName, type ReportField } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { buildPivot, flattenPivot } from '@/lib/pivot'
import { cn } from '@/lib/utils'

type Row = Record<string, unknown>

/** Ölçü kolonu: `key` satırdaki anahtar (kolon boyutu varsa `columnKey`), `field` biçim kaynağı. */
export interface PivotMeasureColumn {
  key: string
  label: string
  field: ReportField
}

export interface PivotSelection {
  /** Seçimi yayan parçanın anahtarı (`Selection.source`). */
  source: string
  selections: Selection[]
  onSelect: (input: SelectionInput, additive: boolean) => void
}

interface PivotTableProps {
  rows: Row[]
  /** Açılır düzeyler, sırayla (üye adı = `field.name`). */
  levels: ReportField[]
  /** Yaprak satırlarda ayrıca gösterilen dimension'lar. */
  leafDims: ReportField[]
  columns: PivotMeasureColumn[]
  /** n. resim dimension'ı n. düzeyin logosudur. */
  images?: ReportField[]
  /** Grupları bu kolonun alt toplamına göre sırala; bu kolonda grubun genel toplamdaki payı çubukla gösterilir. */
  sortBy?: { member: string; desc: boolean }
  /** Toplam satırları (döviz başına bir satır); `columns[].key` ile anahtarlı. */
  totals?: Row[]
  /** Karışık dövizli grupların alt toplamı toplanmaz. */
  currencyMember?: string
  /** Verilirse düzey etiketi çapraz filtre yayar. */
  selection?: PivotSelection
  renderValue: (field: ReportField, value: unknown, row?: Row) => ReactNode
}

/**
 * Açılır matris: `levels` iç içe, alt toplamlı; kapalı başlar (Power BI gibi). Kolonlar:
 * düzey etiketi, yaprak dimension'ları, ölçüler. Chevron açar/kapatır; etiket (seçim
 * yayılıyorsa) çapraz filtre seçimidir.
 */
export function PivotTable({
  rows,
  levels,
  leafDims,
  columns,
  images = [],
  sortBy,
  totals,
  currencyMember,
  selection,
  renderValue,
}: PivotTableProps) {
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const members = useMemo(() => levels.map((level) => level.name), [levels])
  const keys = useMemo(() => columns.map((column) => column.key), [columns])
  // Onlarca satır; her render'da yeniden gruplamak ucuz.
  const groups = useMemo(
    () => buildPivot(rows, members, keys, currencyMember, sortBy),
    [rows, members, keys, currencyMember, sortBy],
  )
  // Payın paydası: üst düzey alt toplamların toplamı. Negatif/karışık dövizli toplamda çubuk yok.
  const grandTotal = useMemo(() => {
    if (!sortBy) return undefined
    let sum = 0
    for (const group of groups) {
      const value = group.totals[sortBy.member]
      if (value === null || value === undefined) return undefined
      sum += value
    }
    return sum > 0 ? sum : undefined
  }, [groups, sortBy])
  const hasSelection = selection?.selections.some((item) => item.source === selection.source)
  const toggle = (id: string) =>
    setExpanded((previous) => {
      const next = new Set(previous)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{levels.map((level) => level.label).join(' / ')}</TableHead>
          {leafDims.map((item) => (
            <TableHead key={item.name}>{item.label}</TableHead>
          ))}
          {columns.map((column) => (
            <TableHead key={column.key} className="text-right">
              {column.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {flattenPivot(groups, expanded).map((line, index) => {
          if (line.kind === 'leaf') {
            return (
              <TableRow key={index}>
                <TableCell />
                {leafDims.map((item) => (
                  <TableCell key={item.name}>
                    {renderValue(item, line.row[item.name], line.row)}
                  </TableCell>
                ))}
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    {renderValue(column.field, line.row[column.key], line.row)}
                  </TableCell>
                ))}
              </TableRow>
            )
          }
          const { group } = line
          const open = expanded.has(group.id)
          const image = images[group.depth]
          const logo = image ? group.first[image.name] : undefined
          const Chevron = open ? ChevronDown : ChevronRight
          const level = levels[group.depth]
          const raw = group.first[group.member]
          const value =
            level.type === 'string' && raw !== null && raw !== undefined && raw !== ''
              ? String(raw)
              : undefined
          const selected =
            selection !== undefined &&
            value !== undefined &&
            isSelected(selection.selections, selection.source, shortName(group.member), value)
          const label = formatFieldValue(level, raw)
          // En derin düzeyin altında gösterilecek yaprak boyutu yoksa açılacak bir şey yok.
          const expandable = group.depth < levels.length - 1 || leafDims.length > 0
          return (
            <TableRow
              key={group.id}
              className={cn(
                group.depth === 0 && 'font-medium',
                selected && 'bg-accent',
                hasSelection && !selected && 'opacity-50',
              )}
            >
              {/* Grup adı kaydırılabilir: dar kartta (finans firma özetleri) tablo kart dışına taşıp
                  son sütunu (toplam + pay çubuğu) kırpmasın. */}
              <TableCell className="min-w-32 whitespace-normal">
                <div className="flex items-center gap-2" style={{ paddingLeft: group.depth * 20 }}>
                  {expandable ? (
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={label}
                      // 16 px ok parmakla vurulamıyordu; 32 px dokunma alanı (yandaki 8 px boşluğa taşar).
                      className="relative shrink-0 after:absolute after:-inset-2 after:content-['']"
                      onClick={() => toggle(group.id)}
                    >
                      <Chevron className="text-muted-foreground size-4" />
                    </button>
                  ) : (
                    <span className="size-4 shrink-0" />
                  )}
                  {typeof logo === 'string' && logo && (
                    <img
                      src={logo}
                      alt=""
                      className={cn(
                        'shrink-0 object-contain',
                        group.depth === 0 ? 'h-10 w-14' : 'h-6 w-14 rounded bg-white px-1 py-0.5',
                      )}
                      onError={(event) => {
                        event.currentTarget.hidden = true
                      }}
                    />
                  )}
                  {selection && value !== undefined ? (
                    <button
                      type="button"
                      className="min-w-0 text-left break-words hover:underline focus-visible:underline"
                      onClick={(event) =>
                        selection.onSelect(
                          {
                            source: selection.source,
                            member: shortName(group.member),
                            value,
                            kind: 'value',
                          },
                          event.ctrlKey || event.metaKey,
                        )
                      }
                    >
                      {label}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="min-w-0 text-left break-words"
                      onClick={() => expandable && toggle(group.id)}
                    >
                      {label}
                    </button>
                  )}
                </div>
              </TableCell>
              {leafDims.map((item) => (
                <TableCell key={item.name} />
              ))}
              {columns.map((column) => {
                const total = group.totals[column.key]
                const share =
                  grandTotal !== undefined && column.key === sortBy?.member && total && total > 0
                    ? (total / grandTotal) * 100
                    : undefined
                return (
                  <TableCell key={column.key}>
                    {renderValue(column.field, total, group.first)}
                    {share !== undefined && (
                      <Progress
                        value={share}
                        aria-label={messages.report.share(label)}
                        className="mt-1 ml-auto w-16"
                      />
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          )
        })}
      </TableBody>
      {totals && totals.length > 0 && (
        <TableFooter>
          {totals.map((row, index) => (
            <TableRow key={index} className="font-semibold">
              <TableCell colSpan={1 + leafDims.length}>{messages.report.total}</TableCell>
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {renderValue(column.field, row[column.key], row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableFooter>
      )}
    </Table>
  )
}
